import type {FormEvent, ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {Hint} from '@/components/ui/hint'
import {tope} from '@/lib/campos-texto'
import {validateAccepted, validateEmail} from '@/lib/validation'

/** Compartido por «Agregar método» y la activación del plan: la autorización explícita es
 * requisito legal y no puede divergir entre las dos. */
export type ConsentValues = {email: string; accepted: boolean}
export type ConsentErrors = Partial<Record<keyof ConsentValues, string>>

export const emptyConsent: ConsentValues = {email: '', accepted: false}

export function validateConsent(values: ConsentValues): ConsentErrors {
  const errors: ConsentErrors = {}
  const email = validateEmail(values.email)
  if (email) errors.email = email
  const accepted = validateAccepted(values.accepted)
  if (accepted) errors.accepted = accepted
  return errors
}

/** `prefix` evita colisiones de id/data-testid si el modal y la activación coexisten.
 * Valida al salir del campo y al enviar, con `noValidate` (doctrina de formularios R4/R5). */
export function ConsentForm({
  prefix,
  values,
  errors,
  onChange,
  onBlurField,
  onSubmit,
  showFields = true,
  header,
  children,
}: {
  prefix: string
  values: ConsentValues
  errors: ConsentErrors
  onChange: (patch: Partial<ConsentValues>) => void
  onBlurField: (field: keyof ConsentValues) => void
  onSubmit: (e: FormEvent) => void
  showFields?: boolean
  header?: ReactNode
  children: ReactNode
}) {
  const {t} = useTranslation()
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid={`${prefix}-form`}>
      {header}

      {showFields && (
        <>
          <div className="space-y-2">
            <Label htmlFor={`${prefix}-email`}>{t('planFacturacion.autoDebito.form.emailLabel')}</Label>
            <Input
              id={`${prefix}-email`}
              data-testid={`${prefix}-email`}
              inputMode="email"
              autoComplete="email"
              maxLength={tope('MAIL')}
              value={values.email}
              onChange={(e) => onChange({email: e.target.value})}
              onBlur={() => onBlurField('email')}
              aria-invalid={!!errors.email}
            />
            <FieldError error={errors.email} />
            <Hint>{t('planFacturacion.autoDebito.form.emailHint')}</Hint>
          </div>

          <div className="space-y-2">
            <label className="flex items-start gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                data-testid={`${prefix}-accept`}
                checked={values.accepted}
                onChange={(e) => onChange({accepted: e.target.checked})}
                onBlur={() => onBlurField('accepted')}
                aria-invalid={!!errors.accepted}
                className="mt-0.5"
              />
              <span>{t('planFacturacion.autoDebito.form.acceptTerms')}</span>
            </label>
            <FieldError error={errors.accepted} />
          </div>

          <Hint testId={`${prefix}-pci-note`}>{t('planFacturacion.autoDebito.form.pciNote')}</Hint>
        </>
      )}

      {children}
    </form>
  )
}
